import * as ngCdkOverlay from '@angular/cdk/overlay'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { mapProperties } from '@attain-apps/object-utils'
import { TypeChecker } from '@attain-apps/type-guards'
import * as d3 from 'd3'

import * as state from '../../../../../../../state'

import * as overlays from './overlays'
import * as _rootOutcome from './root-outcome'

/* -------------------------------------------------------------------------- */
/*                                Color Config                                */
/* -------------------------------------------------------------------------- */
// NOTE: should match background from SCSS
const INSIDE_BACKGROUND_COLOR = ds.color.WHITE

/* ---------------------------------- Units --------------------------------- */
const UNIT_COLOR_SCALE = ds.color.Scale.SHADOW
const UNIT_STARTING_TINT = 2
/**
 * Returns background color for the given {@link state.summit.allocations.models.Unit}
 */
function getUnitColor(unit: UnitNodeDatum): ds.color.Color {
  return UNIT_COLOR_SCALE.tintFromDarkest(UNIT_STARTING_TINT + unit.depth)
}
/**
 * Returns background color for the given {@link state.summit.allocations.models.Unit}
 */
function getUnitHierarchyEdgeColor(
  edge: UnitHierarchyEdgeDatum,
): ds.color.Color {
  return getUnitColor(edge.source)
}

const UNIT_HIERARCHY_EDGE_OPACITY = 0.55
const UNIT_ASSIGNMENT_EDGE_OPACITY = 0.2

const UNASSIGNED_UNIT_OPACITY = 0.3

/* -------------------------------- Outcomes -------------------------------- */
const ROOT_OUTCOME_COLOR = ds.color.Scale.SHADOW.neutral
const OUTCOME_COLOR_SCALES = ds.color.Scale.ALL.filter(
  scale => scale !== UNIT_COLOR_SCALE,
)
const OUTCOME_STARTING_TINT = 2

/* -------------------------------------------------------------------------- */
/*                              Animation Config                              */
/* -------------------------------------------------------------------------- */

const DEFAULT_TRANSITION_DURATION = 300
const DEFAULT_TRANSITION_EASE = d3.easeCubicOut
const DEFAULT_TRANSITION = (transition: d3.Transition<any, any, any, any>) =>
  transition.duration(DEFAULT_TRANSITION_DURATION).ease(DEFAULT_TRANSITION_EASE)

/* -------------------------------------------------------------------------- */
/*                             Positioning Config                             */
/* -------------------------------------------------------------------------- */

const HEIGHT = 500
const WIDTH = HEIGHT
const MAX_RADIUS = Math.min(HEIGHT, WIDTH) / 2
const TOP_PADDING = 5

interface PolarCoordinates {
  /** Radians */
  angle: number
  radius: number
}
interface CartesianCoordinates {
  x: number
  y: number
}

/**
 * Transforms {@link PolarCoordinates} to {@link CartesianCoordinates}
 */
function polarToCartesian({
  angle,
  radius,
}: PolarCoordinates): CartesianCoordinates {
  return {
    x: radius * Math.sin(angle),
    // NOTE: in our graph, y axis is flipped from traditional unit circle (up =
    // negative)
    y: -(radius * Math.cos(angle)),
  }
}
/**
 * Transforms {@link CartesianCoordinates} to {@link PolarCoordinates}
 */
function cartesianToPolar({ x, y }: CartesianCoordinates): PolarCoordinates {
  return {
    // NOTE: in our graph, y axis is flipped from traditional unit circle (up =
    // negative)
    angle: Math.atan2(x, -y),
    radius: Math.sqrt(x * x + y * y),
  }
}

/* -------------------------------- Outcomes -------------------------------- */

const DESCENDANT_OUTCOMES_SLICE_GIRTH = 15
/**
 * Determines the gap between adjacent slices within the same layer
 */
const OUTCOMES_PAD_ANGLE = 0.005
/**
 * Determines the gap between slices in adjacent layers
 */
// NOTE: the `4` is an arbitrary number that just seemed to get things to look
// right
const OUTCOMES_PAD_GIRTH = MAX_RADIUS * (OUTCOMES_PAD_ANGLE / (2 * Math.PI)) * 4
/**
 * Computes the radius based on the given radial `y` value
 */
function getOutcomeRadius(y: number): number {
  return MAX_RADIUS - TOP_PADDING - y * DESCENDANT_OUTCOMES_SLICE_GIRTH
}
/**
 * Inner radius of the slice for an {@link state.summit.allocations.models.Outcome}
 */
function getOutcomeInnerRadius(outcome: OutcomeSliceDatum): number {
  return getOutcomeRadius(outcome.y1) + OUTCOMES_PAD_GIRTH
}
/**
 * Outer radius of the slice for an {@link state.summit.allocations.models.Outcome}
 */
function getOutcomeOuterRadius(outcome: OutcomeSliceDatum): number {
  return getOutcomeRadius(outcome.y0)
}
/**
 * Midpoint between {@link getOutcomeInnerRadius} and {@link getOutcomeOuterRadius}
 */
function getOutcomeCenterRadius(outcome: OutcomeSliceDatum): number {
  return (getOutcomeInnerRadius(outcome) + getOutcomeOuterRadius(outcome)) / 2
}
/**
 * Midpoint between {@link getOutcomeInnerRadius} and {@link getOutcomeOuterRadius}
 */
function getOutcomeCenterCartesianPoint(
  outcome: OutcomeSliceDatum,
): CartesianCoordinates {
  return polarToCartesian({
    angle: (outcome.x0 + outcome.x1) / 2,
    radius: getOutcomeCenterRadius(outcome),
  })
}

/* ---------------------------------- Units --------------------------------- */

const UNIT_RADIUS = 15
const UNIT_MARGIN = 20
const UNIT_LABEL_FONT_SIZE = 10
const UNIT_DISTANCE_FROM_ROOT_SCALAR = UNIT_RADIUS * 2 + UNIT_MARGIN

const UNIT_HIERARCHY_EDGE_THICKNESS = 2
const UNIT_ASSIGNMENT_EDGE_THICKNESS = 2

/**
 * Used to achieve the desired {@link UNIT_MARGIN}
 */
function getUnitDistanceFromRoot(unit: UnitNodeDatum): number {
  return UNIT_DISTANCE_FROM_ROOT_SCALAR * unit.y0
}
/**
 * Calculates the {@link CartesianCoordinates} of the given {@link state.summit.allocations.models.Unit} that
 * achieves the {@link getUnitDistanceFromRoot}
 */
function getUnitCenterPolarPoint(unit: UnitNodeDatum): PolarCoordinates {
  return {
    angle: (unit.x0 + unit.x1) / 2,
    radius: getUnitDistanceFromRoot(unit),
  }
}
/**
 * Calculates the {@link CartesianCoordinates} of the given {@link state.summit.allocations.models.Unit} that
 * achieves the {@link getUnitDistanceFromRoot}
 */
function getUnitCenterCartesianPoint(
  unit: UnitNodeDatum,
): CartesianCoordinates {
  return polarToCartesian(getUnitCenterPolarPoint(unit))
}
/**
 * Calculates the {@link CartesianCoordinates} where the label of the given
 * {@link state.summit.allocations.models.Unit} should be added
 */
function getUnitLabelCartesianPoint(unit: UnitNodeDatum): CartesianCoordinates {
  const point = getUnitCenterCartesianPoint(unit)
  // NOTE: not clear why the -2 is needed to get it centered
  point.y += UNIT_LABEL_FONT_SIZE / 2 - 2
  return point
}
/**
 * Calculates the {@link CartesianCoordinates} of the given {@link state.summit.allocations.models.Unit}
 * after accounting for the current {@link d3.ZoomTransform}
 */
function getUnitTransformedCartesianPoint(
  unit: UnitNodeDatum,
): CartesianCoordinates {
  const point = getUnitCenterCartesianPoint(unit)
  if (unit.transform) {
    point.x = unit.transform.applyX(point.x)
    point.y = unit.transform.applyY(point.y)
  }
  return point
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Graph in Summit used to visualize the allocation of your team against your strategic goals
 */
@ngCore.Component({
  imports: [
    _rootOutcome.Component,
    overlays.outcome.Component,
    overlays.unit.Component,
  ],
  selector: 'atn-summit-allocation-graph',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** {@link HTMLElement} which should contain the {@link d3} graph */
  private readonly _containerElementRefSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('container')

  /**
   * Just the {@link ngCore.ElementRef.nativeElement} from {@link _containerElementRefSg}
   */
  private readonly _containerElementSg = ngCore.computed(
    (containerElementRef = this._containerElementRefSg()) =>
      containerElementRef?.nativeElement,
  )

  /**
   * All of the {@link state.summit.allocations.models.Outcome} to show in this {@link Component}
   */
  public readonly outcomesSg = ngCore.input.required<
    readonly state.summit.allocations.models.Outcome[]
  >({ alias: 'outcomes' })

  /**
   * All of the {@link state.summit.allocations.models.Outcome} to show in this {@link Component}
   */
  public readonly unitsSg = ngCore.input.required<
    readonly state.summit.allocations.models.Unit[]
  >({
    alias: 'units',
  })

  /**
   * All of the {@link state.summit.allocations.models.UnitOutcomeAssignment} to show in this {@link Component}
   */
  public readonly unitOutcomeAssignmentsSg = ngCore.input.required<
    readonly state.summit.allocations.models.UnitOutcomeAssignment[]
  >({ alias: 'unitOutcomeAssignments' })

  /** @see {@link state.me.subfeatures.employee.models.Employee.Id} */
  public readonly rootUnitIdSg = ngCore.input<
    state.me.subfeatures.employee.models.Employee.Id | undefined
  >(undefined, {
    alias: 'rootUnitId',
  })

  /**
   * Emitted when a user clicks to navigate to an outcome explore page
   */
  public readonly outcomeLinkClicked = ngCore.output<{
    entityId: ents.core.dms.models.Entity.Id
    recordId: ents.core.dms.data.Record.Id
  }>()

  /**
   * Current {@link state.summit.allocations.models.Outcome} to show as the root
   */
  public readonly rootOutcomeSg = ngCore.signal<
    state.summit.allocations.models.Outcome | undefined
  >(undefined)

  /**
   * Sets {@link rootOutcomeSg} to make sure it is using an item from
   * {@link outcomesSg}
   */
  private readonly _updateRootOutcomeEffect = ngCore.effect(() => {
    const rootOutcome = this.rootOutcomeSg()
    const outcomes = this.outcomesSg()
    if (
      !rootOutcome ||
      !outcomes.some(outcome => outcome.id === rootOutcome.id)
    ) {
      this.rootOutcomeSg.set(
        outcomes.find(outcome => !outcome.parentId) ?? outcomes[0],
      )
    }
  })

  /**
   * Represents the currently hovered {@link state.summit.allocations.models.Outcome}, if there is one
   */
  public readonly hoveredOutcomeSg = ngCore.signal<
    | {
        position: ngCdkOverlay.FlexibleConnectedPositionStrategyOrigin
        data: state.summit.allocations.models.Outcome
      }
    | undefined
  >(undefined)

  /**
   * Current {@link state.summit.allocations.models.Unit} to show as the root
   */
  public readonly rootUnitSg = ngCore.signal<
    state.summit.allocations.models.Unit | undefined
  >(undefined)

  /**
   * Sets {@link rootUnitSg} to make sure it is using an item from
   * {@link unitsSg}, and preselects based on {@link rootUnitIdSg}
   */
  private readonly _updateRootUnitEffect = ngCore.effect(() => {
    const rootUnit = this.rootUnitSg()
    const units = this.unitsSg()
    const rootUnitId = this.rootUnitIdSg()

    if (!rootUnit || !units.some(unit => unit.id === rootUnit.id)) {
      // Preselect the specified unit if available, otherwise choose the root
      // unit or first available unit
      this.rootUnitSg.set(
        rootUnitId
          ? units.find(unit => unit.id === rootUnitId)
          : (units.find(unit => !unit.parentId) ?? units[0]),
      )
    }
  })

  /**
   * Represents the currently hovered {@link state.summit.allocations.models.Unit}, if there is one
   */
  public readonly hoveredUnitSg = ngCore.signal<
    | {
        position: ngCdkOverlay.FlexibleConnectedPositionStrategyOrigin
        data: state.summit.allocations.models.Unit
      }
    | undefined
  >(undefined)

  /**
   * Assigns colors to each {@link outcomesSg}
   */
  private readonly _outcomeColorsSg = ngCore.computed(() => {
    const colorAndScaleLookup: Partial<
      Record<
        state.summit.allocations.models.Outcome['id'],
        {
          inheritableScale?: ds.color.Scale
          color: ds.color.Color
          tint: number
        }
      >
    > = {}
    let numAssignedScales = 0
    let outcome: state.summit.allocations.models.Outcome | undefined
    const outcomes = [...this.outcomesSg()]
    while ((outcome = outcomes.shift())) {
      if (!outcome.parentId) {
        // If this is the root, we use the root color
        colorAndScaleLookup[outcome.id] = {
          color: ROOT_OUTCOME_COLOR,
          // This is 1 before the first color used from a given scale
          tint: OUTCOME_STARTING_TINT - 1,
        }
        continue
      }
      const parentInfo = colorAndScaleLookup[outcome.parentId]
      if (!parentInfo) {
        // If this is a child and we haven't processed the parent yet, we put it
        // at the end so that we have a chance to process the parent first
        outcomes.push(outcome)
        continue
      }
      // We get the scale from the parent, if we can. Otherwise, we assign a new
      // scale
      const scale =
        parentInfo.inheritableScale ??
        OUTCOME_COLOR_SCALES[numAssignedScales++ % OUTCOME_COLOR_SCALES.length]
      // And we add one tint from the parent
      const tint = parentInfo.tint + 1
      colorAndScaleLookup[outcome.id] = {
        color: scale.tintFromDarkest(tint),
        inheritableScale: scale,
        tint,
      }
    }

    return mapProperties(colorAndScaleLookup, info => info?.color)
  })

  /**
   * All data used in the {@link _updateGraphEffect} to populate the
   * {@link _elementsSg}
   */
  private readonly _dataSg = ngCore.computed(() => {
    const rootOutcome = this.rootOutcomeSg()
    const outcomes = this.outcomesSg()
    const rootUnit = this.rootUnitSg()
    const units = this.unitsSg()
    const assignments = this.unitOutcomeAssignmentsSg()

    /* -------------------------------- Outcomes -------------------------------- */

    const outcomeHierarchy = d3
      .hierarchy(rootOutcome ?? outcomes[0], outcome =>
        outcomes.filter(otherOutcome => otherOutcome.parentId === outcome.id),
      )
      .sum(outcome =>
        outcomes.some(otherOutcome => otherOutcome.parentId === outcome.id)
          ? 0
          : 1,
      )
      .sort(
        (a, b) =>
          outcomes.findIndex(outcome => outcome.id === a.data.id) -
          outcomes.findIndex(outcome => outcome.id === b.data.id),
      )

    const outcomeRoot = d3
      .partition<state.summit.allocations.models.Outcome>()
      // This places the outcomes in polar coordinates where the radius is just
      // equal to depth (not pixels)
      .size([2 * Math.PI, outcomeHierarchy.height + 1])(outcomeHierarchy)

    /* ---------------------------------- Units --------------------------------- */

    /**
     * Used for sorting {@link state.summit.allocations.models.Unit} to be close to their assigned {@link state.summit.allocations.models.Outcome}
     */
    const averageAssignedOutcomeAngleByUnit: Partial<
      Record<state.summit.allocations.models.Unit['id'], number>
    > = {}
    for (const unit of units) {
      const assignedOutcomeAngles = assignments
        .filter(({ unitId }) => unitId === unit.id)
        .map(({ outcomeId }) =>
          outcomeRoot.find(outcome => outcome.data.id === outcomeId),
        )
        .filter(TypeChecker.isNotNullish.raw)
        .map(({ x0, x1 }) => (x0 + x1) / 2)

      averageAssignedOutcomeAngleByUnit[unit.id] =
        assignedOutcomeAngles.reduce((sum, angle) => sum + angle, 0) /
        assignedOutcomeAngles.length
    }

    const unitHierarchy = d3
      .hierarchy(rootUnit ?? units[0], unit =>
        units.filter(otherunit => otherunit.parentId === unit.id),
      )
      .sum(unit =>
        units.some(otherunit => otherunit.parentId === unit.id) ? 0 : 1,
      )
      .sort((a, b) => {
        const aAngle = averageAssignedOutcomeAngleByUnit[a.data.id] ?? 0
        const bAngle = averageAssignedOutcomeAngleByUnit[b.data.id] ?? 0
        return aAngle - bAngle
      })

    const unitRoot = d3
      .partition<state.summit.allocations.models.Unit>()
      // This places the units in polar coordinates where the radius is just
      // equal to depth (not pixels)
      .size([2 * Math.PI, unitHierarchy.height + 1])(unitHierarchy)

    /* ------------------------------- Assignments ------------------------------ */

    const unitAssignments = assignments
      .map<UnitAssignmentEdgeDatum | undefined>(assignment => {
        const outcome = outcomeRoot.find(
          _outcome => _outcome.data.id === assignment.outcomeId,
        )
        const unit = unitRoot.find(_unit => _unit.data.id === assignment.unitId)
        return outcome && unit && { outcome, unit }
      })
      .filter(TypeChecker.isNotNullish.raw)

    return {
      outcomeHierarchy,
      outcomeRoot,
      unitAssignments,
      unitHierarchy,
      unitRoot,
    }
  })

  /**
   * All of the elements in the {@link d3} chart
   *
   * @see {@link ElementSelection}
   */
  private readonly _elementsSg = ngCore.computed(
    (container = this._containerElementSg()) => {
      if (!container) {
        return undefined
      }

      /* ------------------------------ Event Helpers ----------------------------- */
      /**
       * Applies the given {@link d3.ZoomTransform}
       *
       * Pass `undefined` to reset to no zoom/pan
       */
      function applyZoom(transform: d3.ZoomTransform | undefined): void {
        if (transform === undefined) {
          mainContentBackground.call(
            mainContentZoom.transform.bind(mainContentZoom),
            d3.zoomIdentity,
          )
        }

        // Store transformation on the units
        unitNodeCircles.select().each(unit => {
          unit.transform = transform
        })

        // Transform the entire nodes group
        unitNodesGroup
          .transition()
          .call(DEFAULT_TRANSITION)
          .attr('transform', transform?.toString() ?? null)

        // Refreshes all relevant elements based on the transformed node
        // positions
        unitNodeCircles.refresh()
        unitNodeLabels.refresh()
        unitAssignmentEdges.refresh()
        unitHierarchyEdges.refresh()
      }

      /**
       * Moves the given {@link state.summit.allocations.models.Unit} by the given amount
       */
      function moveUnit(
        unit: UnitNodeDatum,
        change: Pick<d3.D3DragEvent<any, any, any>, 'dx' | 'dy'>,
      ): void {
        // Calculate new position
        const desiredCartesianPosition = getUnitCenterCartesianPoint(unit)
        desiredCartesianPosition.x += change.dx
        desiredCartesianPosition.y += change.dy

        const desiredPolarPosition = cartesianToPolar(desiredCartesianPosition)
        const oldPolarPosition = getUnitCenterPolarPoint(unit)
        const angleDelta = desiredPolarPosition.angle - oldPolarPosition.angle
        const radiusDelta =
          (desiredPolarPosition.radius - oldPolarPosition.radius) /
          UNIT_DISTANCE_FROM_ROOT_SCALAR

        // Update the node's stored position
        unit.x0 += angleDelta
        unit.x1 += angleDelta
        unit.y0 += radiusDelta
        unit.y1 += radiusDelta

        // Refresh everything affected by the node's position
        unitNodeCircles.refresh(_selection =>
          _selection.filter(unitNode => unitNode.data.id === unit.data.id),
        )
        unitNodeLabels.refresh(_selection =>
          _selection.filter(unitNode => unitNode.data.id === unit.data.id),
        )
        unitHierarchyEdges.refresh(_selection =>
          _selection.filter(
            link => link.source === unit || link.target === unit,
          ),
        )
        unitAssignmentEdges.refresh(_selection =>
          _selection.filter(assignment => assignment.unit === unit),
        )
      }

      /* --------------------------------- Set Up --------------------------------- */
      const d3Container = d3.select(container)
      // Remove old elements from container
      d3Container.selectAll('*').remove()
      // Create new SVG
      const svg = d3Container
        .append('svg')
        .style('position', 'absolute')
        .style('width', '100%')
        .style('height', '100%')
        .attr('viewBox', [-WIDTH / 2, -HEIGHT / 2, WIDTH, HEIGHT])
      // Creates clip path and background circle for the main content
      const mainContentClipPath = svg
        .append('defs')
        .append('clipPath')
        .attr('id', 'clip')
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', MAX_RADIUS - TOP_PADDING)
      const mainContentGroup = svg.append('g').attr('clip-path', 'url(#clip)')
      const mainContentZoom = d3
        .zoom<SVGCircleElement, unknown>()
        .on('zoom', (event: d3.D3ZoomEvent<SVGCircleElement, unknown>) =>
          applyZoom(event.transform),
        )
      const mainContentBackground = mainContentGroup
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', MAX_RADIUS - TOP_PADDING)
        .attr('fill', INSIDE_BACKGROUND_COLOR)
        .call(mainContentZoom)

      /* -------------------------------------------------------------------------- */
      /*                                Main Content                                */
      /* -------------------------------------------------------------------------- */

      // NOTE: order matters - later elements appear over earlier elements

      /* ---------------------------------- Edges --------------------------------- */

      const unitAssignmentEdges = new ElementSelection<
        SVGLineElement,
        UnitAssignmentEdgeDatum
      >(
        'line',
        mainContentGroup,
        assignment =>
          `${assignment.unit.data.id}-${assignment.outcome.data.id}`,
        assignments => ({ data: assignments }),
        selection =>
          selection
            .attr('stroke-width', UNIT_ASSIGNMENT_EDGE_THICKNESS)
            .style('opacity', UNIT_ASSIGNMENT_EDGE_OPACITY)
            .transition()
            .call(DEFAULT_TRANSITION)
            .attr('stroke', d => {
              const outcomeColors = this._outcomeColorsSg()
              return outcomeColors[d.outcome.data.id] ?? ROOT_OUTCOME_COLOR
            })
            .attr('x1', d => getUnitTransformedCartesianPoint(d.unit).x)
            .attr('y1', d => getUnitTransformedCartesianPoint(d.unit).y)
            .attr('x2', d => getOutcomeCenterCartesianPoint(d.outcome).x)
            .attr('y2', d => getOutcomeCenterCartesianPoint(d.outcome).y),
      )
      const unitHierarchyEdges = new ElementSelection<
        SVGLineElement,
        UnitHierarchyEdgeDatum,
        d3.HierarchyNode<state.summit.allocations.models.Unit>
      >(
        'line',
        mainContentGroup,
        edge => `${edge.source.data.id}-${edge.target.data.id}`,
        unitHierarchy => ({
          // NOTE: the types don't convey that the links are referring to the
          // specific nodes in `unitHierarchy`, which are actually
          // `UnitHierarchyEdgeDatum`, hence the casting
          data: unitHierarchy.links() satisfies readonly d3.HierarchyLink<state.summit.allocations.models.Unit>[] as unknown as readonly UnitHierarchyEdgeDatum[],
        }),
        selection =>
          selection
            .attr('stroke-width', UNIT_HIERARCHY_EDGE_THICKNESS)
            .style('opacity', UNIT_HIERARCHY_EDGE_OPACITY)
            .transition()
            .call(DEFAULT_TRANSITION)
            .attr('stroke', d => getUnitHierarchyEdgeColor(d))
            .attr('x1', d => getUnitTransformedCartesianPoint(d.source).x)
            .attr('y1', d => getUnitTransformedCartesianPoint(d.source).y)
            .attr('x2', d => getUnitTransformedCartesianPoint(d.target).x)
            .attr('y2', d => getUnitTransformedCartesianPoint(d.target).y),
      )

      /* ---------------------------------- Units --------------------------------- */

      function getUnitOpacity(unit: UnitNodeDatum): number {
        return unitAssignmentEdges.data.some(edge => edge.unit === unit)
          ? 1
          : UNASSIGNED_UNIT_OPACITY
      }

      const unitNodesGroup = mainContentGroup.append('g')
      const unitNodeCircles = new ElementSelection<
        SVGCircleElement,
        UnitNodeDatum,
        d3.HierarchyRectangularNode<state.summit.allocations.models.Unit>
      >(
        'circle',
        unitNodesGroup,
        unit => unit.data.id,
        unitRoot => ({ data: unitRoot.descendants() }),
        selection => {
          selection
            .transition()
            .call(DEFAULT_TRANSITION)
            .attr('cx', d => getUnitCenterCartesianPoint(d).x)
            .attr('cy', d => getUnitCenterCartesianPoint(d).y)
          selection
            .style('cursor', 'pointer')
            .attr('fill', d => getUnitColor(d))
            .style('opacity', getUnitOpacity)
            .attr('r', UNIT_RADIUS)
            .on('mouseenter', (event, d) => {
              this.hoveredUnitSg.set({
                data: d.data,
                position: { x: event.clientX, y: event.clientY },
              })
            })
            .on('mouseleave', () => {
              this.hoveredUnitSg.set(undefined)
            })
            .on('dblclick', (event, d) => {
              this.rootUnitSg.update(rootUnit => {
                if (d.data.id !== rootUnit?.id) {
                  // As long as they have not clicked on the root, we just
                  // drill into the selected item
                  return d.data
                }
                if (!rootUnit.parentId) {
                  // If they clicked on the root, but the root has no parent,
                  // we can't go up any further
                  return rootUnit
                }
                // If they clicked on the root and it has a parent, we try to
                // find that parent. If we can't we stay on the current root
                return (
                  this.unitsSg().find(unit => unit.id === rootUnit.parentId) ??
                  rootUnit
                )
              })
              applyZoom(undefined)
            })
            .call(
              d3
                .drag<SVGCircleElement, UnitNodeDatum>()
                .on(
                  'drag',
                  (
                    event: d3.D3DragEvent<
                      SVGCircleElement,
                      UnitNodeDatum,
                      unknown
                    >,
                    d,
                  ) => {
                    moveUnit(d, event)
                  },
                ),
            )
        },
      )

      const unitNodeLabels = new ElementSelection<
        SVGTextElement,
        UnitNodeDatum,
        d3.HierarchyRectangularNode<state.summit.allocations.models.Unit>
      >(
        'text',
        unitNodesGroup,
        unit => unit.data.id,
        unitRoot => ({ data: unitRoot.descendants() }),
        selection =>
          selection
            .transition()
            .call(DEFAULT_TRANSITION)
            .attr('dx', d => getUnitLabelCartesianPoint(d).x)
            .attr('dy', d => getUnitLabelCartesianPoint(d).y)
            .attr('fill', d => ds.color.font(getUnitColor(d)))
            .style('opacity', getUnitOpacity)
            .text(d => d.data.code),
      )
      unitNodeLabels.group
        .attr('class', 'no-text-selection')
        .attr('pointer-events', 'none')
        .attr('text-anchor', 'middle')
        .attr('font-size', `${UNIT_LABEL_FONT_SIZE}px`)

      /* -------------------------------- Outcomes -------------------------------- */

      const outcomeArc = d3
        .arc<OutcomeSliceDatum>()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .padAngle(OUTCOMES_PAD_ANGLE)
        .padRadius(d => getOutcomeCenterRadius(d))
        .innerRadius(d => getOutcomeInnerRadius(d))
        .outerRadius(d => getOutcomeOuterRadius(d))

      const outcomeNodes = new ElementSelection<
        SVGPathElement,
        OutcomeSliceDatum,
        d3.HierarchyRectangularNode<state.summit.allocations.models.Outcome>
      >(
        'path',
        mainContentGroup,
        outcome => outcome.data.id,
        outcomeRoot => ({
          data: outcomeRoot.descendants(),
        }),
        selection => {
          selection
            // .transition().call(DEFAULT_TRANSITION)
            .attr('d', outcomeArc)
          selection
            .attr(
              'fill',
              // NOTE: every outcome should have an assigned color, but if
              // somehow we find one that doesn't, we use `ROOT_OUTCOME_COLOR`
              d => this._outcomeColorsSg()[d.data.id] ?? ROOT_OUTCOME_COLOR,
            )
            .style('cursor', 'pointer')
            .on('mouseenter', (event, d) => {
              this.hoveredOutcomeSg.set({
                data: d.data,
                position: { x: event.clientX, y: event.clientY },
              })
            })
            .on('mouseleave', () => {
              this.hoveredOutcomeSg.set(undefined)
            })
            .on('dblclick', (event, d) => {
              this.rootOutcomeSg.update(rootOutcome => {
                if (d.data.id !== rootOutcome?.id) {
                  // As long as they have not clicked on the root, we just
                  // drill into the selected item
                  return d.data
                }
                if (!rootOutcome.parentId) {
                  // If they clicked on the root, but the root has no parent,
                  // we can't go up any further
                  return rootOutcome
                }
                // If they clicked on the root and it has a parent, we try to
                // find that parent. If we can't we stay on the current root
                return (
                  this.outcomesSg().find(
                    outcome => outcome.id === rootOutcome.parentId,
                  ) ?? rootOutcome
                )
              })
              applyZoom(undefined)
            })
        },
      )

      /* ----------------------------------- End ---------------------------------- */

      return {
        outcomeNodes,
        unitAssignmentEdges,
        unitHierarchyEdges,
        unitNodeCircles,
        unitNodeLabels,
      }
    },
  )

  /**
   * Updates the data in the {@link d3} graph
   */
  private readonly _updateGraphEffect = ngCore.effect(() => {
    const elements = this._elementsSg()
    if (!elements) {
      return
    }
    const {
      outcomeNodes,
      unitAssignmentEdges,
      unitHierarchyEdges,
      unitNodeCircles,
      unitNodeLabels,
    } = elements

    const { outcomeRoot, unitAssignments, unitHierarchy, unitRoot } =
      this._dataSg()

    unitAssignmentEdges.setData(unitAssignments)
    unitHierarchyEdges.setData(unitHierarchy)
    unitNodeCircles.setData(unitRoot)
    unitNodeLabels.setData(unitRoot)
    outcomeNodes.setData(outcomeRoot)
  })

  /**
   * Handles the outcome link click event from the root outcome component
   */
  public onOutcomeLinkClicked(event: {
    entityId: ents.core.dms.models.Entity.Id
    recordId: ents.core.dms.data.Record.Id
  }): void {
    this.outcomeLinkClicked.emit(event)
  }
}

/* -------------------------------------------------------------------------- */
/*                                Helper Types                                */
/* -------------------------------------------------------------------------- */

type OutcomeSliceDatum =
  d3.HierarchyRectangularNode<state.summit.allocations.models.Outcome>

interface UnitNodeDatum
  extends d3.HierarchyRectangularNode<state.summit.allocations.models.Unit> {
  transform?: d3.ZoomTransform
}

interface UnitAssignmentEdgeDatum {
  unit: UnitNodeDatum
  outcome: OutcomeSliceDatum
}

type UnitHierarchyEdgeDatum = Replace.Properties.BasedOnType<
  d3.HierarchyLink<state.summit.allocations.models.Unit>,
  d3.HierarchyNode<state.summit.allocations.models.Unit>,
  UnitNodeDatum
>

/**
 * Manages a set of elements in the {@link d3} graph
 */
class ElementSelection<
  TElement extends SVGElement,
  TDatum,
  TData = readonly TDatum[],
  TExtra = unknown,
> {
  public readonly group: d3.Selection<SVGGElement, unknown, null, undefined>

  public get data(): readonly TDatum[] {
    return this._extracted?.data ?? []
  }

  private _extracted: ({ data: readonly TDatum[] } & TExtra) | undefined

  constructor(
    // TODO: replace positional arguments with params object
    public readonly tag: string,
    public readonly parent: d3.Selection<any, unknown, null, undefined>,
    private readonly _identify: (datum: TDatum) => string,
    private readonly _extract: (
      data: TData,
    ) => { data: readonly TDatum[] } & TExtra,
    private readonly _render: (
      selection: d3.Selection<TElement, TDatum, SVGGElement, unknown>,
      extracted: ({ data: readonly TDatum[] } & TExtra) | undefined,
    ) => void,
  ) {
    this.group = parent.append('g')
  }

  public select(): d3.Selection<TElement, TDatum, SVGGElement, unknown> {
    return this.group.selectAll<TElement, TDatum>(this.tag)
  }

  public setData(data: TData): void {
    this._extracted = this._extract(data)
    this.select().data(this._extracted.data, this._identify).join(this.tag)
    this.refresh()
  }

  public refresh(
    selection:
      | d3.Selection<TElement, TDatum, SVGGElement, unknown>
      | ((
          selection: d3.Selection<TElement, TDatum, SVGGElement, unknown>,
        ) => d3.Selection<
          TElement,
          TDatum,
          SVGGElement,
          unknown
        >) = this.select(),
  ): void {
    if (typeof selection === 'function') {
      selection = selection(this.select())
    }
    this._render(selection, this._extracted)
  }
}
